import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useChildren } from "@/context/ChildrenContext";
import { ACTIVITIES } from "@/data/parentingData";
import SouvenirPhoto from "@/components/SouvenirPhoto";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImagePlus, Trash2, Sparkles, Clock } from "lucide-react";
import Mascot from "@/components/Mascot";
import { toast } from "sonner";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function AlbumPage() {
  const { kids } = useChildren();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [targetId, setTargetId] = useState(null);
  const [childFilter, setChildFilter] = useState("tous");
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/activities/completed");
    setRows(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const pickFile = (id) => {
    setTargetId(id);
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !targetId) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image trop lourde (10 Mo maximum)");
      return;
    }
    setUploadingId(targetId);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/activities/${targetId}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo ajoutée à l'album 📸");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi de la photo impossible");
    } finally {
      setUploadingId(null);
      setTargetId(null);
    }
  };

  const removePhoto = async (id) => {
    await api.delete(`/activities/${id}/photo`);
    toast.success("Photo retirée");
    load();
  };

  const assignChild = async (id, childId) => {
    await api.patch(`/activities/${id}/child`, { child_id: childId || null });
    toast.success(childId ? "Souvenir rattaché 💛" : "Souvenir détaché");
    load();
  };

  const matchesFilter = (r) =>
    childFilter === "tous" ||
    (childFilter === "aucun" ? !r.child_id : r.child_id === childFilter);

  const withPhoto = rows.filter((r) => r.photo_path && matchesFilter(r));
  const withoutPhoto = rows.filter((r) => !r.photo_path && matchesFilter(r));
  const unassignedCount = rows.filter((r) => r.photo_path && !r.child_id).length;

  const detailsOf = (activityId) => ACTIVITIES.find((a) => a.id === activityId);

  return (
    <div className="space-y-8" data-testid="album-section">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={upload}
        className="hidden"
        data-testid="album-file-input"
      />

      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Album souvenirs
          </h1>
          <p className="mt-2 text-[#77849C]">
            Une photo pour chaque activité réalisée ensemble — votre carnet de moments partagés.
          </p>
        </div>
        <Mascot pose="camera" size={88} anim="float" className="hidden sm:block shrink-0 mascot-pop" />
      </div>

      {loading ? (
        <div className="soft-card p-8 text-center text-[#77849C]">Chargement de l'album…</div>
      ) : rows.length === 0 ? (
        <div className="soft-card p-10 text-center" data-testid="album-empty-state">
          <div className="flex justify-center"><Mascot pose="camera" size={150} anim="wiggle" className="mascot-pop" /></div>
          <p className="mt-4 text-[#44506B]">Aucune activité réalisée pour le moment.</p>
          <Link to="/activites" className="brand-btn mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5">
            <Sparkles className="h-4 w-4" /> Choisir une activité
          </Link>
        </div>
      ) : (
        <>
          {kids.length > 0 && (
            <div className="filter-row no-scrollbar" data-testid="album-child-filters">
              {[
                { id: "tous", label: "Tous les souvenirs" },
                ...kids.map((k) => ({ id: k.id, label: k.name })),
                ...(unassignedCount > 0 ? [{ id: "aucun", label: `Sans enfant (${unassignedCount})` }] : []),
              ].map((o) => (
                <button
                  key={o.id}
                  data-testid={`album-filter-${o.id}`}
                  onClick={() => setChildFilter(o.id)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                    childFilter === o.id
                      ? "bg-[#23375E] text-white"
                      : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}

          <section>
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-3">
              {withPhoto.length} souvenir{withPhoto.length > 1 ? "s" : ""} en photo
            </p>
            {withPhoto.length === 0 ? (
              <div className="soft-card p-6 text-[#77849C]" data-testid="album-no-photo-yet">
                Vos activités réalisées sont listées ci-dessous : ajoutez-y une photo pour démarrer l'album.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="album-photo-grid">
                {withPhoto.map((r) => (
                  <figure key={r.id} data-testid={`album-item-${r.id}`} className="soft-card overflow-hidden p-0 group">
                    <button onClick={() => setZoom(r)} className="block w-full">
                      <SouvenirPhoto
                        completionId={r.id}
                        alt={r.activity_title}
                        className="h-40 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                      />
                    </button>
                    <figcaption className="p-3">
                      <p className="font-semibold text-[#16233D] text-sm leading-tight">{r.activity_title}</p>
                      {kids.length > 0 && (
                        <select
                          data-testid={`album-child-select-${r.id}`}
                          value={r.child_id || ""}
                          onChange={(e) => assignChild(r.id, e.target.value)}
                          className="mt-2 w-full rounded-full border border-[#E3E8F1] bg-[#EDF2F9] px-2.5 py-1 text-xs font-medium text-[#23375E] outline-none focus:border-[#23375E]"
                        >
                          <option value="">Rattacher à un enfant…</option>
                          {kids.map((k) => (
                            <option key={k.id} value={k.id}>{k.name}</option>
                          ))}
                        </select>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-[#99A5BB]">{formatDate(r.photo_at || r.created_at)}</span>
                        <button
                          data-testid={`album-delete-${r.id}`}
                          onClick={() => removePhoto(r.id)}
                          className="text-[#99A5BB] hover:text-red-500 transition-colors duration-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
          </section>

          {withoutPhoto.length > 0 && (
            <section>
              <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-3">
                Activités réalisées sans photo
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="album-pending-list">
                {withoutPhoto.map((r) => {
                  const d = detailsOf(r.activity_id);
                  return (
                    <div
                      key={r.id}
                      data-testid={`album-pending-${r.id}`}
                      className="soft-card p-4 flex items-center gap-3"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-[#16233D]">{r.activity_title}</p>
                        <div className="mt-1 flex items-center gap-3 text-xs text-[#99A5BB]">
                          <span>{formatDate(r.created_at)}</span>
                          {r.child_name && <span className="text-[#23375E] font-medium">{r.child_name}</span>}
                          {d && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {d.duration}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        data-testid={`album-add-photo-${r.id}`}
                        onClick={() => pickFile(r.id)}
                        disabled={uploadingId === r.id}
                        className="brand-btn rounded-full px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
                      >
                        <ImagePlus className="h-4 w-4" />
                        {uploadingId === r.id ? "Envoi…" : "Ajouter une photo"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      <Dialog open={!!zoom} onOpenChange={(v) => !v && setZoom(null)}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[88vh] overflow-y-auto" data-testid="album-zoom-dialog">
          <DialogHeader>
            <DialogTitle>{zoom?.activity_title}</DialogTitle>
            <DialogDescription>Photo souvenir de cette activité réalisée en famille.</DialogDescription>
          </DialogHeader>
          {zoom && (
            <div>
              <SouvenirPhoto
                completionId={zoom.id}
                alt={zoom.activity_title}
                className="w-full rounded-2xl object-contain max-h-[65vh]"
              />
              <p className="mt-3 text-sm text-[#99A5BB]">{formatDate(zoom.photo_at || zoom.created_at)}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
